import { t, jt } from 'ttag'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import isEmail from 'validator/lib/isEmail'
import { useMutation } from 'react-query'
import { useDispatch } from 'react-redux'
import { useContext, useMemo, useCallback } from 'react'

import { api, errors } from '@h2/api'
import { Globals } from '@h2/globals'
import { Field } from '@h2/components/inputs/field'
import { TextInput } from '@h2/components/inputs/text'
import { AutoSuggest } from '@h2/components/inputs/autoSuggest'
import { Select } from '@h2/components/inputs/select'
import { Switch } from '@h2/components/inputs/switch'
import { Button } from '@h2/components/buttons/button'

import {
  permissions,
  primaryTypes,
  counselorTypes,
  educatorTypes,
  studentTypes,
  k12EducatorTypes,
  k12Populations,
  k12Programs,
  isNavigator,
  hasSchool,
  getItems,
  initItem
} from './utils'
import { Form, Actions } from './components'

import { useForm } from '../hooks/useForm'
import { US_STATES_LIST } from '../../constants'
import {
  statesNameValueMap,
  statesAbbreviationValueMap,
  getStateFromAbbreviation
} from '../../utils/states'
import {
  augmentSchools,
  loadSchools,
  renderSchool,
  getFutureGraduationItems
} from '../../utils/schools'
import { setUser } from '../../actions/setUser'

const Fieldset = styled.fieldset`
  display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
  margin-top: 15px;

  > legend {
    color: black;
    font-size: 16px;
    font-weight: bold;
  }
`
const createLanguageItems = () => [
  { value: '', label: t`Automatic` },
  { value: 'en_US', label: t`English` },
  { value: 'es_US', label: t`Spanish` }
]
const initSchool = school => {
  if (school?.name) {
    return augmentSchools([school]).shift()
  }
}
const primaryTypeItems = getItems(primaryTypes)
const counselorTypeItems = getItems(counselorTypes)
const educatorTypeItems = getItems(educatorTypes)
const studentTypeItems = getItems(studentTypes)
const k12EducatorTypeItems = getItems(k12EducatorTypes)
const k12PopulationItems = getItems(k12Populations)
const k12ProgramItems = getItems(k12Programs)
const Edit = ({ user, onSuccess, onError, submitText = t`Save` }) => {
  const {
    user: editor,
    locale,
    defaultRegion,
    accessTypes,
    schools,
    seniorYear,
    externalIdProperties,
    isNavianceElementary
  } = useContext(Globals)
  const dispatch = useDispatch()
  const isEditingSelf = useMemo(() => user.id === editor.id, [user, editor])
  const usernameIsEmail = useMemo(() => user.userName === user.email, [user])
  const languageItems = useMemo(createLanguageItems, [locale])
  const eidName = useMemo(
    () => externalIdProperties?.name ?? t`External ID`,
    [externalIdProperties?.name]
  )
  const canEditEmail = useMemo(
    () => !user.hasManager && !user.educatorAccountGranted,
    [user]
  )
  const canGrantPermission = useMemo(
    () =>
      !user.isRoot &&
      editor.id !== user.id &&
      (editor.hasAdmin || (editor.hasSchoolAdmin && !user.hasAdmin)),
    [user, editor]
  )
  const permissionItems = useMemo(
    () => accessTypes.map(({ id, name }) => ({ value: id, label: name })),
    [accessTypes]
  )
  const [schoolItems, loadSchoolItems] = useMemo(() => {
    if (schools.length) {
      return [augmentSchools(schools)]
    }

    return [undefined, loadSchools]
  }, [schools])
  const graduationYearItems = useMemo(
    () => getFutureGraduationItems(seniorYear, isNavianceElementary),
    [seniorYear, isNavianceElementary]
  )
  const {
    register,
    handleOnSubmit,
    setValidation,
    error,
    isInvalid,
    required,
    value: formValues
  } = useForm({
    validators: {
      firstName(form) {
        if (isNavigator(form.primaryType?.value) && !form.firstName?.trim()) {
          return t`First Name is required`
        }
      },
      lastName(form) {
        if (isNavigator(form.primaryType?.value) && !form.lastName?.trim()) {
          return t`Last Name is required`
        }
      },
      async email(form, checkAvailability = false) {
        const emailEntered = form.email?.trim()
        const isStudentWithUsername =
          form.primaryType?.value === primaryTypes.student.id &&
          user.userName &&
          !usernameIsEmail

        if (emailEntered) {
          if (!isEmail(emailEntered)) {
            return t`Invalid Email address`
          }

          if (usernameIsEmail) {
            if (/^___/.test(emailEntered)) {
              return t`Invalid format, Email can not start with "___"`
            }

            if (checkAvailability) {
              let isAvailable = false

              try {
                isAvailable = await api.fetch('/auth/usernameAvailable', {
                  method: 'POST',
                  body: JSON.stringify(emailEntered)
                })
              } catch {
                return t`An error occurred while checking availability`
              }

              if (!isAvailable) {
                return t`Already in use`
              }
            }
          }
        } else if (usernameIsEmail || !isStudentWithUsername) {
          return t`Email is required`
        }
      },
      state(form) {
        if (!form.state?.trim()) {
          return t`State is required`
        }
      },
      school(form) {
        /**
         * No custom input for sites with configured schools.
         * A `name` without an `id` can only happen from custom input.
         */
        if (schoolItems && form.school?.name && !form.school.id) {
          return `Please select a school from the list`
        }
      },
      graduationYear(form) {
        const entered = formValues.graduationYear
        const parsed = form.graduationYear

        if (entered) {
          const maxYear = new Date().getFullYear() + 24

          if (isNaN(parsed) || parsed.toString().length !== 4) {
            return t`Graduation Year must be a four digit number`
          }

          if (parsed < 1920 || parsed > maxYear) {
            return t`Graduation Year must be between 1920 and ${maxYear}`
          }
        }
      },
      eid(form) {
        if (
          (externalIdProperties?.required ?? form.eid) &&
          externalIdProperties?.pattern
        ) {
          const regex = new RegExp(externalIdProperties.pattern)

          if (!regex.test(form.eid)) {
            return externalIdProperties.patternMatchError ?? jt`Invalid ${eidName}`
          }
        }
      }
    },
    initialValues: {
      permission: initItem(user.permission, permissionItems, permissionItems[3]),
      localOpportunitiesAdministrator: user.localOpportunitiesAdministrator,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      state: getStateFromAbbreviation(user.state ?? defaultRegion),
      school: initSchool(user.school),
      eid: user.externalId,
      graduationYear: user.graduationYear,
      languageId: initItem(locale, languageItems, languageItems[0]),
      primaryType: initItem(user.primaryType, primaryTypeItems),
      counselorType: initItem(user.careerCounselorType, counselorTypeItems),
      educatorType: initItem(user.educatorType, educatorTypeItems),
      studentType: initItem(user.studentType, studentTypeItems),
      k12EducatorType: initItem(user.k12EducatorType, k12EducatorTypeItems),
      k12Population: initItem(user.k12Population, k12PopulationItems),
      k12Program: initItem(user.k12Program, k12ProgramItems)
    }
  })
  const { mutate, isLoading } = useMutation(
    body => {
      return api.fetch(`/personalProfile/${isEditingSelf ? 'me' : user.id}`, {
        method: 'POST',
        body: JSON.stringify(body)
      })
    },
    {
      onSuccess: useCallback(
        updatedUser => {
          if (isEditingSelf) {
            dispatch(setUser(updatedUser))
          }

          if (typeof onSuccess === 'function') {
            onSuccess(updatedUser)
          }
        },
        [dispatch, isEditingSelf, onSuccess]
      ),
      onError: useCallback(
        err => {
          const validation = errors.getValidation(err)

          if (validation) {
            setValidation(validation)
          } else if (typeof onError === 'function') {
            onError(err)
          }
        },
        [setValidation, onError]
      )
    }
  )
  const onSubmit = useCallback(
    form => {
      // Changes may have made some form values obsolete.
      const obsolete = Object.keys(required).filter(field => !required[field])

      // Remove obsolete, and empty fields
      Object.keys(form).forEach(field => {
        if (obsolete.includes(field) || Number.isNaN(form[field]) || !form[field]) {
          delete form[field]
        }
      })

      if (isEditingSelf) {
        delete form.permission
      }

      if (user.isRoot) {
        delete form.permission
        delete form.eid
      }

      mutate(form)
    },
    [required, mutate, isEditingSelf, user.isRoot]
  )
  const showLocalOpps = useMemo(
    () =>
      user.hasAdmin &&
      [permissions.manager, permissions.schoolAdministrator].includes(
        formValues.permission
      ),
    [user.hasAdmin, formValues.permission]
  )
  const showEid = useMemo(
    () =>
      Boolean(externalIdProperties) &&
      (externalIdProperties.userDefined || editor.hasManager),
    [editor, externalIdProperties]
  )
  const showSchool = useMemo(
    () => hasSchool({ ...user, primaryType: formValues.primaryType?.value }),
    [user, formValues.primaryType?.value]
  )
  const showPermission = useMemo(
    () => canGrantPermission && accessTypes.length > 0,
    [canGrantPermission, accessTypes.length]
  )
  const showK12Fields = useMemo(
    () => formValues.educatorType?.value === educatorTypes.k12.id,
    [formValues.educatorType?.value]
  )
  const [showStudentType, showCounselorType, showEducatorType] = useMemo(
    () => [
      formValues.primaryType?.value === primaryTypes.student.id,
      formValues.primaryType?.value === primaryTypes.counselor.id,
      formValues.primaryType?.value === primaryTypes.educator.id
    ],
    [formValues.primaryType?.value]
  )
  const isNameRequired = useMemo(
    () => isNavigator(formValues.primaryType?.value),
    [formValues.primaryType?.value]
  )
  const isEmailRequired = useMemo(
    () => usernameIsEmail || !(showStudentType && user.userName && !usernameIsEmail),
    [showStudentType, user.userName, usernameIsEmail]
  )

  return (
    <Form onSubmit={handleOnSubmit(onSubmit)} noValidate>
      <Field
        isRequired
        isVisible={showPermission}
        id="permission-field"
        htmlFor="permission-select"
        desc={error.permission}
        status={error.permission ? 'error' : 'default'}
        label={t`Access`}>
        <Select
          id="permission-select"
          labelledBy="permission-field"
          items={permissionItems}
          {...register('permission', {
            required: showPermission,
            parser: val => val?.value
          })}
        />
      </Field>
      <Field
        isRequired
        id="primary-type-field"
        htmlFor="primary-type-select"
        desc={error.primaryType}
        status={error.primaryType ? 'error' : 'default'}
        label={t`User Type`}>
        <Select
          id="primary-type-select"
          labelledBy="primary-type-field"
          items={primaryTypeItems}
          {...register('primaryType', { required: true, parser: val => val?.value })}
        />
      </Field>
      <Field
        isRequired={isNameRequired}
        label={t`First Name`}
        desc={error.firstName}
        status={error.firstName ? 'error' : 'default'}>
        <TextInput {...register('firstName', { required: isNameRequired })} />
      </Field>
      <Field
        isRequired={isNameRequired}
        label={t`Last Name`}
        desc={error.lastName}
        status={error.lastName ? 'error' : 'default'}>
        <TextInput {...register('lastName', { required: isNameRequired })} />
      </Field>
      <Field
        isRequired={isEmailRequired}
        label={t`Email`}
        desc={canEditEmail ? error.email : t`Validated emails cannot be changed`}
        status={error.email ? 'error' : 'default'}>
        <TextInput
          isDisabled={!canEditEmail}
          {...register('email', {
            required: isEmailRequired,
            runAsyncCheck: user.email !== formValues.email
          })}
        />
      </Field>
      <Field id="language-field" htmlFor="language-select" label={t`Preferred Language`}>
        <Select
          id="language-select"
          labelledBy="language-field"
          items={languageItems}
          {...register('languageId', { parser: val => val?.value })}
        />
      </Field>
      <Field
        isVisible={showStudentType}
        id="student-type-field"
        htmlFor="student-type-select"
        desc={error.studentType}
        status={error.studentType ? 'error' : 'default'}
        label={t`Student Type`}>
        <Select
          id="student-type-select"
          labelledBy="student-type-field"
          items={studentTypeItems}
          {...register('studentType', {
            parser: val => val?.value
          })}
        />
      </Field>
      <Field
        isVisible={showCounselorType}
        id="counselor-type-field"
        htmlFor="counselor-type-select"
        desc={error.counselorType}
        status={error.counselorType ? 'error' : 'default'}
        label={t`Counselor Type`}>
        <Select
          id="counselor-type-select"
          labelledBy="counselor-type-field"
          items={counselorTypeItems}
          {...register('counselorType', {
            parser: val => val?.value
          })}
        />
      </Field>
      <Field
        isVisible={showEducatorType}
        id="educator-type-field"
        htmlFor="educator-type-select"
        desc={error.educatorType}
        status={error.educatorType ? 'error' : 'default'}
        label={t`Educator Type`}>
        <Select
          id="educator-type-select"
          labelledBy="educator-type-field"
          items={educatorTypeItems}
          {...register('educatorType', {
            parser: val => val?.value
          })}
        />
      </Field>
      <Field
        id="graduation-field"
        htmlFor="graduation-select"
        isVisible={showStudentType}
        desc={error.graduationYear}
        status={error.graduationYear ? 'error' : 'default'}
        label={t`Graduation Year`}>
        <AutoSuggest
          id="graduation-select"
          labelledBy="graduation-field"
          placeholder={t`Start typing for suggestions...`}
          items={graduationYearItems}
          {...register('graduationYear', {
            parser: val => parseInt(val?.value ?? val, 10)
          })}
        />
      </Field>
      <Field
        isVisible={showSchool}
        id="school-field"
        htmlFor="school-select"
        desc={
          error.school ??
          t`If you don't have a primary school, you can leave this field blank.`
        }
        status={error.school ? 'error' : 'default'}
        label={t`School`}>
        <AutoSuggest
          id="school-select"
          labelledBy="school-field"
          placeholder={t`Start typing for suggestions...`}
          items={schoolItems}
          loadItems={loadSchoolItems}
          renderItem={renderSchool}
          {...register('school', {
            parser: val => (val?.name ? val : { name: val ?? '' })
          })}
        />
      </Field>
      <Field
        isRequired
        id="states-field"
        htmlFor="states-select"
        label={t`State`}
        desc={error.state}
        status={error.state ? 'error' : 'default'}>
        <AutoSuggest
          id="states-select"
          labelledBy="states-field"
          items={US_STATES_LIST}
          placeholder={t`Enter your state...`}
          {...register('state', {
            required: true,
            parser: val =>
              val?.value ??
              statesNameValueMap.get(val?.trim().toLowerCase()) ??
              statesAbbreviationValueMap.get(val?.trim().toLowerCase())
          })}
        />
      </Field>
      <Field
        isVisible={showEid}
        isRequired={Boolean(externalIdProperties?.required)}
        label={eidName}
        desc={error.eid}
        status={error.eid ? 'error' : 'default'}>
        <TextInput {...register('eid', { required: externalIdProperties?.required })} />
      </Field>
      <Field
        isRequired
        isVisible={showEducatorType && showK12Fields}
        id="k12educator-type-field"
        htmlFor="k12educator-type-select"
        desc={error.k12EducatorType}
        status={error.k12EducatorType ? 'error' : 'default'}
        label={t`K12 Educator Type`}>
        <Select
          id="k12educator-type-select"
          labelledBy="k12educator-type-field"
          items={k12EducatorTypeItems}
          {...register('k12EducatorType', {
            required: showEducatorType && showK12Fields,
            parser: val => val?.value
          })}
        />
      </Field>
      <Field
        isRequired
        isVisible={showEducatorType && showK12Fields}
        id="k12population-field"
        htmlFor="k12population-select"
        desc={error.k12Population}
        status={error.k12Population ? 'error' : 'default'}
        label={t`K12 Population`}>
        <Select
          id="k12population-select"
          labelledBy="k12population-field"
          items={k12PopulationItems}
          {...register('k12Population', {
            required: showEducatorType && showK12Fields,
            parser: val => val?.value
          })}
        />
      </Field>
      <Field
        isRequired
        isVisible={showEducatorType && showK12Fields}
        id="k12program-field"
        htmlFor="k12program-select"
        desc={error.k12Program}
        status={error.k12Program ? 'error' : 'default'}
        label={t`K12 Program`}>
        <Select
          id="k12program-select"
          labelledBy="k12program-field"
          items={k12ProgramItems}
          {...register('k12Program', {
            required: showEducatorType && showK12Fields,
            parser: val => val?.value
          })}
        />
      </Field>
      <Fieldset isVisible={showLocalOpps}>
        <legend>{t`Local Opportunities Administrator`}</legend>
        <Switch
          label={[t`No`, t`Yes`]}
          isOn={formValues.localOpportunitiesAdministrator}
          {...register('localOpportunitiesAdministrator', {
            binary: true,
            required: showLocalOpps
          })}
        />
      </Fieldset>
      <Actions>
        <Button
          type="submit"
          size="large"
          align="center"
          isLoading={isLoading}
          isDisabled={isInvalid || isLoading}>
          {submitText}
        </Button>
      </Actions>
    </Form>
  )
}

Edit.propTypes = {
  user: PropTypes.object.isRequired,
  /** The text on the form's submit button. Defaults to "Save". */
  submitText: PropTypes.string,
  /** Callback when edit is successful, `onSuccess(updatedUser)`. */
  onSuccess: PropTypes.func,
  /** Callback when edit errored, `onError(error)`. */
  onError: PropTypes.func
}

export { Edit }
